<script setup lang="ts">
import { computed, ref } from "vue";
import { PopoverClose } from "reka-ui";

import { useMessageStore, useRetentionStore } from "@/stores";
import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import RetentionActionPopover from "./RetentionActionPopover.vue";
import RetentionActionConfirmCase from "./RetentionActionConfirmCase.vue";

import { isApiError } from "@/services/Api";
import { getDateISOString, dateInputToUTCDateTime } from "@/services/UtilitiesService";

withDefaults(defineProps<{ originalDate: Date; overrideDate?: Date }>(), {  })

const caseRouteParams = useCaseRouteParams();
const retentionStore = useRetentionStore();

const startDateInputId = `override-start-date-${caseRouteParams.value.casename}`;

const startDateValue = ref(getDateISOString(__props.overrideDate ?? __props.originalDate));
const startDate = computed(() => {
  if (startDateValue.value === "") return null;
  return dateInputToUTCDateTime(startDateValue.value);
});

const caseRefInput = ref("");

const state = ref<
  { status: "idle" } | { status: "loading"; action: "clear" | "override" } | { status: "error"; message: string }
>({ status: "idle" });

const isSubmitDisabled = computed(
  () => state.value.status === "loading" || caseRefInput.value !== caseRouteParams.value.casename,
);

async function clearOverrideStartDate() {
  state.value = { status: "loading", action: "clear" };

  try {
    await retentionStore.overrideStartDate({ startDate: null });

    state.value = { status: "idle" };

    useMessageStore().updateMessage({
      type: "success",
      autoHide: true,
      showReload: false,
      title: "Case Retention",
      message: "Retention start date override cleared successfully.",
    });
  } catch (error) {
    const message =
      isApiError<{ reason?: string }>(error) && error.status === 406 && error.data?.reason
        ? error.data.reason
        : "Failed to clear override start date. Please try again.";

    state.value = { status: "error", message };

    useMessageStore().updateMessage({
      type: "error",
      autoHide: true,
      showReload: false,
      title: "Case Retention",
      message,
    });
  }
}

async function handleSubmit() {
  if (isSubmitDisabled.value) return;

  state.value = { status: "loading", action: "override" };

  try {
    if (startDate.value == null || startDate.value.getTime() === (__props.overrideDate ?? __props.originalDate).getTime()) {
      throw new Error("No changes made to the start date.");
    }

    await retentionStore.overrideStartDate({ startDate: startDate.value.toISOString() });

    state.value = { status: "idle" };

    useMessageStore().updateMessage({
      type: "success",
      autoHide: true,
      showReload: false,
      title: "Case Retention",
      message: "Retention start date overridden successfully.",
    });
  } catch (error) {
    const message =
      isApiError<{ reason?: string }>(error) && error.status === 406 && error.data?.reason
        ? error.data.reason
        : error instanceof Error
          ? error.message
          : "Failed to override start date. Please try again.";

    state.value = { status: "error", message };

    useMessageStore().updateMessage({
      type: "error",
      autoHide: true,
      showReload: false,
      title: "Case Retention",
      message,
    });
  }
}
</script>
<template>
  <RetentionActionPopover>
    <template #trigger>
      <button type="button" class="tw-btn-md-gray tw-b-r">Override Start Date</button>
    </template>
    <form class="tw-flex tw-flex-col tw-gap-2" @submit.prevent="handleSubmit">
      <p class="tw-max-w-md tw-text-balance tw-text-sm">
        Override the retention start date for this case. This affects when the case expires.
      </p>
      <hr />
      <dl class="tw-grid tw-grid-cols-[max-content_1fr] tw-items-center tw-gap-x-4 tw-gap-y-1 tw-text-sm">
        <dt class="tw-text-xs">Original</dt>
        <dd>{{ originalDate.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) }}</dd>
      </dl>
      <hr />
      <div class="tw-flex tw-flex-col tw-gap-1">
        <label :for="startDateInputId" class="tw-text-xs"> New Start Date</label>
        <input
          :id="startDateInputId"
          v-model="startDateValue"
          type="date"
          required
          :max="getDateISOString(new Date())"
          class="tw-w-full tw-rounded tw-border tw-p-2 tw-text-sm"
        />
      </div>
      <hr />
      <RetentionActionConfirmCase v-model:case-ref="caseRefInput" :require-reason="false" />
      <p v-if="state.status === 'error'" role="alert" class="tw-text-sm tw-text-red-600">
        {{ state.message }}
      </p>
      <div class="tw-flex tw-items-center tw-gap-1">
        <button
          type="button"
          class="tw-btn-md-red tw-b-r tw-flex tw-items-center tw-gap-1"
          :disabled="isSubmitDisabled"
          @click="clearOverrideStartDate"
        >
          <i
            v-if="state.status === 'loading' && state.action === 'clear'"
            aria-hidden="true"
            class="fas fa-spin fa-circle-notch"
          />
          Reset
        </button>
        <div class="tw-flex-auto" />
        <PopoverClose class="tw-btn-md-white tw-b-r">Cancel</PopoverClose>
        <button
          type="submit"
          class="tw-btn-md-gray tw-b-r tw-flex tw-items-center tw-gap-1"
          :disabled="isSubmitDisabled"
        >
          <i
            v-if="state.status === 'loading' && state.action === 'override'"
            aria-hidden="true"
            class="fas fa-spin fa-circle-notch"
          />
          Override Start Date
        </button>
      </div>
    </form>
  </RetentionActionPopover>
</template>
